import { cx, useTranslations } from "@jugl-web/utils";
import React from "react";
import { ReactComponent as Arrow1Icon } from "./assets/arrow-1.svg";
import { ReactComponent as Arrow2Icon } from "./assets/arrow-2.svg";
import { ReactComponent as Arrow3Icon } from "./assets/arrow-3.svg";
import { ReactComponent as Arrow4Icon } from "./assets/arrow-4.svg";
import { ReactComponent as Arrow5Icon } from "./assets/arrow-5.svg";
import { ReactComponent as Arrow6Icon } from "./assets/arrow-6.svg";

const cardIndexToArrow: Partial<
  Record<number, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>
> = {
  1: Arrow1Icon,
  2: Arrow2Icon,
  3: Arrow3Icon,
  4: Arrow4Icon,
  5: Arrow5Icon,
  6: Arrow6Icon,
};

const cardIndexToArrowOffset: Partial<Record<number, string>> = {
  1: "translate-y-[8px]",
  2: "translate-y-[7px]",
  3: "translate-y-[14px]",
  4: "translate-y-[13px]",
  5: "translate-y-[12px]",
  6: "translate-y-[12px]",
};

interface ChannelCardProps {
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  index: number;
  name: string;
  chatsCount: number;
  messages: number;
  isSelected?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export const ChannelCard: React.FC<ChannelCardProps> = ({
  icon,
  name,
  index,
  chatsCount,
  messages,
  isSelected,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { t } = useTranslations();
  const ArrowIcon = cardIndexToArrow[index];

  return (
    <div
      className={cx(
        "relative flex cursor-pointer items-center gap-8 rounded-xl border border-solid bg-white p-5 transition-colors",
        isSelected
          ? "border-[#1A2023] text-[#1A2023]"
          : "border-[#DEDEE4] text-[#DEDEE4]"
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex w-[186px] items-center gap-2">
        {React.createElement(icon, {
          className: "h-12 w-12 shrink-0",
        })}
        <span className="font-secondary text-base font-medium text-[#1A2023]">
          {name}
        </span>
      </div>
      <div className="h-full w-px self-stretch bg-[#F2F2F2]" />
      <div className="flex flex-1 flex-col gap-0.5 font-secondary">
        <span className="whitespace-nowrap text-[#1A2023]">
          <span className="text-base leading-[1.4] font-semibold">
            {chatsCount}
          </span>{" "}
          <span className="text-sm leading-[1.4] font-normal text-[#2C3438]">
            {t(
              {
                id: "cx-analytics.chats-count-label",
                defaultMessage: "{count, plural, one {chat} other {chats}}",
              },
              { count: chatsCount }
            )}
          </span>
        </span>
        <span className="whitespace-nowrap text-[#1A2023]">
          <span className="text-base leading-[1.4] font-semibold">
            {messages}
          </span>{" "}
          <span className="text-sm leading-[1.4] font-normal text-[#2C3438]">
            {t(
              {
                id: "cx-analytics.messages-label",
                defaultMessage:
                  "{count, plural, one {message} other {messages}}",
              },
              { count: messages }
            )}
          </span>
        </span>
      </div>
      {ArrowIcon &&
        React.createElement(ArrowIcon, {
          className: cx(
            "absolute right-1 translate-x-full",
            index > 2 ? "bottom-1/2" : "top-1/2",
            cardIndexToArrowOffset[index],
            isSelected && "z-10"
          ),
        })}
    </div>
  );
};
